import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AppUserAuth } from 'src/app/core/interfaces/auth/login.interfaces';
import { AuthService } from '../auth/auth.service';
import { Globals } from '../globals/globals';

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private injector: Injector,
    private globals: Globals,
  ) {}

  /**
   * Add the access token to the header of each request
   */
  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        AppOrigin: this.globals.appOrigin,
      },
    });
  }

  /**
   * Intercept all http requests
   * @param request
   * @param next
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<
    | HttpSentEvent
    | HttpHeaderResponse
    | HttpProgressEvent
    | HttpResponse<any>
    | HttpUserEvent<any>
    | any
  > {
    const errorHandler = this.injector.get(ErrorHandler);
    const authService = this.injector.get(AuthService);

    // intercept http requests
    // add the token to header request
    // handle the result when it triggered an error
    return next.handle(this.addTokenToRequest(request, authService.token)).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          switch ((<HttpErrorResponse>err).status) {
            // catch 401 to refresh token
            case 401:
              return this.handle401Error(
                request,
                next,
                errorHandler,
                authService,
              );

            // catch 0 and send it to the errorhandler to show offline message
            case 0:
              errorHandler.handleError(err);
              return throwError(err);

            // other errors should be passed on
            default:
              this.handleGeneralError(err);
              return throwError(err);
          }
        } else {
          return throwError(err);
        }
      }),
    );
  }

  /**
   * Handle 401 error - refresh access token
   * @param request
   * @param next
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    errorHandler: ErrorHandler,
    authService: AuthService,
  ) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);

      return authService.refreshToken().pipe(
        switchMap((user: AppUserAuth | null) => {
          // handle the result of the refresh token
          if (user) {
            this.tokenSubject.next(user.accessToken);
            localStorage.setItem(
              this.globals.localStorageTokenName_luqas,
              JSON.stringify(user),
            );
            return next.handle(
              this.addTokenToRequest(request, user.accessToken),
            );
          }

          errorHandler.handleError(new Error('No new refresh token received.'));
          authService.logout();
          return next.handle(request);
        }),
        catchError((err) => {
          errorHandler.handleError(err);
          authService.logout();
          return next.handle(request);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }),
      );
    } else {
      this.isRefreshingToken = false;

      return this.tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenToRequest(request, token));
        }),
      );
    }
  }

  /**
   * Create a general error object and returns it
   * @param error The error that is thrown
   */
  private handleGeneralError(
    error: HttpErrorResponse,
  ): Observable<ServicesError> {
    const dataError = new ServicesError();
    dataError.message =
      error.error.message !== undefined ? error.error.message : error.message;
    return throwError(dataError);
  }
}

/**
 * Error object for services
 */
export class ServicesError {
  message: string;
}
